import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';

export default function CustomQuotePage() {
  const [form, setForm] = useState({
    email: '',
    scope: '',
    codebaseSize: '',
    repoLink: '',
    deadline: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1000);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-28 pb-20 px-6 lg:px-8">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold text-stone-900 mb-4">
              Quote Request Received
            </h1>
            <p className="text-stone-500 leading-relaxed mb-8">
              Thank you for reaching out. We'll review your requirements and
              send a custom quote to{' '}
              <span className="font-semibold text-stone-700">{form.email}</span>{' '}
              within 1–2 business days.
            </p>
            <Link to="/">
              <Button
                variant="outline"
                className="border-stone-300 text-stone-700"
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-28 pb-20 px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors duration-200 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="w-6 h-6 text-amber-500" />
            <h1 className="text-3xl font-bold text-stone-900">
              Custom Quote
            </h1>
          </div>
          <p className="text-stone-500 mb-8">
            For larger codebases, ongoing engagements, or specific requirements.
            Tell us about your needs and we'll prepare a custom proposal.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <Label
                htmlFor="cq-email"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Email Address <span className="text-rose-500">*</span>
              </Label>
              <Input
                id="cq-email"
                type="email"
                required
                placeholder="you@company.com"
                value={form.email}
                onChange={handleChange('email')}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
              />
            </div>

            {/* Scope */}
            <div>
              <Label
                htmlFor="cq-scope"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Brief Scope Description{' '}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="cq-scope"
                placeholder="Describe what you need reviewed, specific goals, concerns, or areas of focus..."
                value={form.scope}
                onChange={handleChange('scope')}
                rows={4}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20 resize-none"
              />
            </div>

            {/* Codebase size */}
            <div>
              <Label
                htmlFor="cq-size"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Estimated Codebase Size{' '}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="cq-size"
                placeholder="e.g., ~3000 lines, 15 files, entire Django app"
                value={form.codebaseSize}
                onChange={handleChange('codebaseSize')}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
              />
            </div>

            {/* Repo link */}
            <div>
              <Label
                htmlFor="cq-repo"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Repository Link{' '}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="cq-repo"
                type="url"
                placeholder="https://github.com/your-org/your-repo"
                value={form.repoLink}
                onChange={handleChange('repoLink')}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
              />
            </div>

            {/* Deadline */}
            <div>
              <Label
                htmlFor="cq-deadline"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Preferred Deadline{' '}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="cq-deadline"
                placeholder="e.g., Within 2 weeks, No rush, ASAP"
                value={form.deadline}
                onChange={handleChange('deadline')}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !form.email}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white text-base font-semibold shadow-md shadow-amber-500/20 transition-[background-color,box-shadow] duration-200 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Submit Custom Request
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
